import {
  Card,
  CardContent,
  Switch,
  View,
  Input,
  Button,
  Image,
} from "@/components/ui";
import { Wallet } from "@/components/shared/icons";
import { Typography } from "@/components/shared/typography";
import { useAuthState } from "@/store/auth";
import { formatPrice } from "@/lib/helpers/number";
import { InputFormControl } from "@/components/shared/input-form-control";
import type { Control } from "react-hook-form";
import type { ClientApplyCreditFormData } from "@/store/profile/forms/clientApplyCreditForm";
import { useInvoiceState } from "@/store/invoices";
import { getAccountCreditBalance, getDepartmentCreditConfig } from "../../lib";
import { CreditTypeEnum } from "@/__generated__/graphql";
import { cn, getPlatform } from "@/lib/utils";
import { CurrencyCircleDollar } from "@/components/shared/icons";
import { useSharerModalStore } from "@/store/sharer";
import { useRoute } from "@/components/shared/router";
import { useClientCreditAccounts } from "@/gqlhooks/profile/useClientCreditAccounts";
import { DepartmentCreditType } from "../type";

export function ApplyCredit({
  control,
  onApplyAllMain,
  onApplyAllMassage,
  disableApplyAllMain,
  disableApplyAllMassage,
  departmentCreditType,
}: {
  control: Control<ClientApplyCreditFormData>;
  onApplyAllMain: () => void;
  onApplyAllMassage: () => void;
  disableApplyAllMain: boolean;
  disableApplyAllMassage: boolean;
  departmentCreditType: DepartmentCreditType | null;
}) {
  const userInfoId = useAuthState((user) => user.data.userInfo.id);
  const { creditAccounts } = useClientCreditAccounts(userInfoId);

  const { setOpen } = useSharerModalStore();
  const { push } = useRoute();
  const isWeb = getPlatform() === "web";

  const {
    data: { applyMainCredit, applyMassageCredit },
    setApplyMainCredit,
    setApplyMassageCredit,
    showPaymentMethodModal,
  } = useInvoiceState();

  const mainBalance = getAccountCreditBalance({
    creditAccounts,
    type: CreditTypeEnum.General,
  });

  const departmentCreditConfig =
    getDepartmentCreditConfig(departmentCreditType);

  const departmentBalance =
    creditAccounts.find(
      (credit) =>
        credit.creditType === CreditTypeEnum.Department &&
        credit.linkedObject?.__typename === "Department" &&
        credit.linkedObject.name === departmentCreditConfig?.linkedName,
    )?.balance ?? 0;

  const isNoMainCreditBalance = mainBalance === 0;
  const isNoCreditBalance = departmentBalance === 0;

  const handleBuyMassageCredit = () => {
    if (!departmentCreditConfig) return;
    if (isWeb) {
      showPaymentMethodModal();
    }
    push({
      pageKey: departmentCreditConfig.pageKey,
    });
  };

  return (
    <View className="flex flex-col gap-3">
      {/* Main Credit Account */}
      <Card
        className={cn(
          applyMainCredit && "border-primary-border",
          "z-10 bg-white",
        )}
      >
        <CardContent className="flex flex-col gap-3">
          <View className="flex flex-row items-center gap-2 md:gap-3">
            <View className="flex w-full flex-1 flex-row gap-3">
              <Wallet weight="fill" className="text-primary" size={28} />
              <View className="flex flex-col gap-1">
                <Typography variant="body-lg">Apply main credit</Typography>
                <View className="flex flex-row gap-1">
                  <Typography variant="body-md" color="foreground-intermediate">
                    Balance:
                  </Typography>
                  <Typography variant="body-md" color="brand-primary">
                    {formatPrice(mainBalance)}
                  </Typography>
                </View>
              </View>
            </View>
            <Switch
              checked={applyMainCredit}
              onCheckedChange={(checked) => {
                setApplyMainCredit(checked);
              }}
              disabled={isNoMainCreditBalance}
            />
          </View>
          {applyMainCredit && (
            <View className="flex flex-row items-start gap-2">
              <InputFormControl
                name="mainCreditBalance"
                component={Input}
                control={control}
                className="w-full flex-1"
                componentProps={{
                  prefix: <Typography>$</Typography>,
                  placeholder: "Input Balance",
                }}
              />
              <Button
                variant="secondary"
                color="CTA2"
                rounded="full"
                fullWidth="full"
                className="mb-3 h-11 max-w-32"
                onClick={onApplyAllMain}
                children="Apply all"
                disabled={disableApplyAllMain}
              />
            </View>
          )}
        </CardContent>
      </Card>

      {/* Main Credit Banner */}
      {isNoMainCreditBalance && (
        <View className="native:bg-primary -mt-9 overflow-hidden rounded-xl bg-gradient-to-r from-[#3091ffde] to-[#308FFF]">
          <View className="flex flex-row items-center gap-1 bg-[url('@/assets/images/banner-blue-bg.png')] p-2 pt-8">
            <CurrencyCircleDollar weight="fill" color="white" size={16} />
            <Typography variant="body-sm" color="white">
              Earn credits by referring friends!
            </Typography>
            <Button
              variant="tertiary"
              color="CTA2"
              rounded="full"
              className="ml-auto"
              size="xs"
              children="Share"
              onClick={() => setOpen(true)}
            />
          </View>
        </View>
      )}

      {/* Department Credit account */}
      {departmentCreditConfig && (
        <Card
          className={cn(
            applyMassageCredit && "border-primary-border",
            "z-10 bg-white",
          )}
        >
          <CardContent className="flex flex-col gap-3">
            <View className="flex flex-row items-center gap-2 md:gap-3">
              <View className="flex w-full flex-1 flex-row gap-3">
                <Image
                  src={departmentCreditConfig.icon}
                  className="size-7"
                  width={28}
                  height={28}
                />

                <View className="flex flex-col gap-1">
                  <Typography variant="body-lg">
                    {`Apply ${departmentCreditConfig.label.toLowerCase()} credit`}
                  </Typography>
                  <View className="flex flex-row gap-1">
                    <Typography
                      variant="body-md"
                      color="foreground-intermediate"
                    >
                      Balance:
                    </Typography>
                    <Typography variant="body-md" color="brand-primary">
                      {formatPrice(departmentBalance)}
                    </Typography>
                  </View>
                </View>
              </View>
              <Switch
                checked={applyMassageCredit}
                onCheckedChange={(checked) => {
                  setApplyMassageCredit(checked);
                }}
                disabled={isNoCreditBalance}
              />
            </View>
            {applyMassageCredit && (
              <View className="flex flex-row items-start gap-2">
                <InputFormControl
                  name="massageCreditBalance"
                  component={Input}
                  control={control}
                  className="w-full flex-1"
                  componentProps={{
                    prefix: <Typography>$</Typography>,
                    placeholder: "Input Balance",
                  }}
                />
                <Button
                  variant="secondary"
                  color="CTA2"
                  rounded="full"
                  fullWidth="full"
                  className="mb-3 h-11 max-w-32"
                  onClick={onApplyAllMassage}
                  children="Apply all"
                  disabled={disableApplyAllMassage}
                />
              </View>
            )}
          </CardContent>
        </Card>
      )}

      {/* Department Credit Banner */}
      {departmentCreditConfig && isNoCreditBalance && (
        <View className="native:bg-primary -mt-9 w-full flex-1 overflow-hidden rounded-xl bg-gradient-to-r from-[#3091ffde] to-[#308FFF]">
          <View className="flex flex-1 flex-row items-center gap-1 bg-[url('@/assets/images/banner-blue-bg.png')] p-2 pt-8">
            <CurrencyCircleDollar weight="fill" color="white" size={16} />
            <Typography variant="body-sm" color="white">
              Buy more credit and get up to 10% bonus.
            </Typography>
            <Button
              variant="subtle"
              color="white"
              iconColor="white"
              iconAlignment="end"
              iconName="plus"
              rounded="full"
              iconSize="sm"
              className="ml-auto"
              size="xs"
              children="Buy Credits"
              onClick={handleBuyMassageCredit}
            />
          </View>
        </View>
      )}
    </View>
  );
}
